import { RNG } from "rot-js";
import { LevelMap } from "../../../engine/map/LevelMap";
import { Bounds } from "../../../engine/space/bounds";
import { AdjTypes, FloorTypes } from "../Map";
import { ShrineMap } from "../ShrineMap";

export const fillEdges = (map: ShrineMap) => {
  const botRow = (map.height - 1) * map.width;
  const oneMinusBot = (map.height - 2) * map.width;
  const right = map.width - 1;
  const oneMinusRight = map.width - 2;

  for (let x = 0; x < map.width; x++) {
    map.tiles[x].adj.up = AdjTypes.WALL;
    map.tiles[x + oneMinusBot].adj.down = AdjTypes.WALL;
    map.tiles[x + botRow].adj.up = AdjTypes.WALL;
    map.tiles[x + botRow].type = FloorTypes.OOB;
  }

  for (let y = 0; y < map.height; y++) {
    map.tiles[y * map.width].adj.left = AdjTypes.WALL;
    map.tiles[oneMinusRight + y * map.width].adj.right = AdjTypes.WALL;
    map.tiles[right + y * map.width].adj.left = AdjTypes.WALL;
    map.tiles[right + y * map.width].type = FloorTypes.OOB;
  }
};

export const setAreaZLevel = (map: ShrineMap, bounds: Bounds, z: number) => {
  for (let y = bounds.y; y < bounds.h + bounds.y; y++) {
    for (let x = bounds.x; x < bounds.w + bounds.x; x++) {
      map.tiles[x + y * map.width].z = z;
    }
  }
};

export const raiseAndDivideArea = (
  map: LevelMap<FloorTypes, AdjTypes>,
  bounds: Bounds,
  z: number
) => {
  const bot = bounds.h + bounds.y - 1;
  for (let y = bounds.y; y < bounds.h + bounds.y; y++) {
    map.tiles[y * map.width + bounds.x].adj.left = AdjTypes.ONE_OUT;
    map.tiles[y * map.width + bounds.x - 1].adj.right = AdjTypes.ONE_IN;

    map.tiles[y * map.width + bounds.x + bounds.w - 1].adj.right =
      AdjTypes.WALL;
    map.tiles[y * map.width + bounds.x + bounds.w].adj.left = AdjTypes.WALL;

    for (let x = bounds.x; x < bounds.w + bounds.x; x++) {
      map.tiles[x + y * map.width].z = z;
      if (y === bounds.y) {
        map.tiles[x + y * map.width].adj.up = AdjTypes.ONE_OUT;
        map.tiles[x + (y - 1) * map.width].adj.down = AdjTypes.ONE_IN;
      }

      if (y === bot) {
        map.tiles[x + y * map.width].adj.down = AdjTypes.WALL;
        map.tiles[x + (y + 1) * map.width].adj.up = AdjTypes.WALL;
      }
    }
  }
};

export const makeRect = (
  map: LevelMap<FloorTypes, AdjTypes>,
  bounds: Bounds,
  z: number
) => {
  const bot = bounds.h + bounds.y - 1;
  for (let y = bounds.y; y < bounds.h + bounds.y; y++) {
    map.tiles[y * map.width + bounds.x].adj.left = AdjTypes.WALL;
    if (0 < bounds.x) {
      map.tiles[y * map.width + bounds.x - 1].adj.right = AdjTypes.WALL;
    }

    map.tiles[y * map.width + bounds.x + bounds.w - 1].adj.right =
      AdjTypes.WALL;
    map.tiles[y * map.width + bounds.x + bounds.w].adj.left = AdjTypes.WALL;

    for (let x = bounds.x; x < bounds.w + bounds.x; x++) {
      map.tiles[y * map.width + x].type = FloorTypes.EMPTY;
      map.tiles[y * map.width + x].z = z;

      if (y === bounds.y) {
        map.tiles[x + y * map.width].adj.up = AdjTypes.WALL;
        if (0 < y) {
          map.tiles[x + (y - 1) * map.width].adj.down = AdjTypes.WALL;
        }
      }

      if (y === bot) {
        map.tiles[x + y * map.width].adj.down = AdjTypes.WALL;
        map.tiles[x + (y + 1) * map.width].adj.up = AdjTypes.WALL;
      }
    }
  }
};

const getHorzRandom = (rng: typeof RNG, a: Bounds, b: Bounds) => {
  const minX = Math.max(a.x, b.x);
  const maxX = Math.min(a.x + a.w, b.x + b.w);
  if (maxX - minX < 3) {
    return rng.getUniformInt(minX, maxX);
  } else {
    return rng.getUniformInt(minX + 1, maxX - 1);
  }
};
const getVertRandom = (rng: typeof RNG, a: Bounds, b: Bounds) => {
  const minY = Math.max(a.y, b.y);
  const maxY = Math.min(a.y + a.h, b.y + b.h);
  if (maxY - minY < 3) {
    return rng.getUniformInt(minY, maxY);
  } else {
    return rng.getUniformInt(minY + 1, maxY - 1);
  }
};

const openSharedVert = (a: Bounds, b: Bounds, cb: (y: number) => void) => {
  let y = Math.max(a.y, b.y);
  const maxY = Math.min(a.y + a.h, b.y + b.h);
  for (; y < maxY; y++) {
    cb(y);
  }
};

const openSharedHorz = (a: Bounds, b: Bounds, cb: (x: number) => void) => {
  let x = Math.max(a.x, b.x);
  const maxX = Math.min(a.x + a.w, b.x + b.w);
  for (; x < maxX; x++) {
    cb(x);
  }
};

export const carveShared = (map: ShrineMap, a: Bounds, b: Bounds) => {
  if (a.x === b.x + b.w) {
    //Left-Right aligned
    openSharedVert(a, b, (y) => {
      map.tiles[a.x + y * map.width].adj.left = AdjTypes.OPEN;
      map.tiles[a.x - 1 + y * map.width].adj.right = AdjTypes.OPEN;
    });
  } else if (a.x + a.w === b.x) {
    //Right-Left aligned
    openSharedVert(a, b, (y) => {
      map.tiles[b.x + y * map.width].adj.right = AdjTypes.OPEN;
      map.tiles[b.x - 1 + y * map.width].adj.left = AdjTypes.OPEN;
    });
  } else if (a.y === b.y + b.h) {
    //Bot-Top aligned
    openSharedHorz(a, b, (x) => {
      map.tiles[x + a.y * map.width].adj.up = AdjTypes.OPEN;
      map.tiles[x + (a.y - 1) * map.width].adj.down = AdjTypes.OPEN;
    });
  } else if (a.y + a.h === b.y) {
    //Top-bot aligned
    openSharedHorz(a, b, (x) => {
      map.tiles[x + b.y * map.width].adj.up = AdjTypes.OPEN;
      map.tiles[x + (b.y - 1) * map.width].adj.down = AdjTypes.OPEN;
    });
  }
};

//The rooms must pass AABB already
export const makeDoor = (
  rng: typeof RNG,
  map: ShrineMap,
  a: Bounds,
  b: Bounds
) => {
  if (a.x === b.x + b.w) {
    //Left-Right aligned
    const x = a.x;
    const y = getVertRandom(rng, a, b);
    map.tiles[x + y * map.width].adj.left = AdjTypes.OPEN;
    map.tiles[x - 1 + y * map.width].adj.right = AdjTypes.OPEN;
  } else if (a.x + a.w === b.x) {
    //Right-Left aligned
    const x = b.x;
    const y = getVertRandom(rng, a, b);
    map.tiles[x + y * map.width].adj.right = AdjTypes.OPEN;
    map.tiles[x - 1 + y * map.width].adj.left = AdjTypes.OPEN;
  } else if (a.y === b.y + b.h) {
    //Bot-Top aligned
    const x = getHorzRandom(rng, a, b);
    const y = a.y;
    map.tiles[x + y * map.width].adj.up = AdjTypes.OPEN;
    map.tiles[x + (y - 1) * map.width].adj.down = AdjTypes.OPEN;
  } else if (a.y + a.h === b.y) {
    //Top-bot aligned
    const x = getHorzRandom(rng, a, b);
    const y = b.y;
    map.tiles[x + y * map.width].adj.up = AdjTypes.OPEN;
    map.tiles[x + (y - 1) * map.width].adj.down = AdjTypes.OPEN;
  }
};

export const carveZDifference = (
  map: ShrineMap,
  a: Bounds,
  b: Bounds,
  aHigher: boolean
) => {
  if (a.x === b.x + b.w) {
    //Left-Right aligned
    openSharedVert(a, b, (y) => {
      map.tiles[a.x + y * map.width].adj.left = aHigher
        ? AdjTypes.ONE_OUT
        : AdjTypes.ONE_IN;
      map.tiles[a.x - 1 + y * map.width].adj.right = aHigher
        ? AdjTypes.ONE_IN
        : AdjTypes.ONE_OUT;
    });
  } else if (a.x + a.w === b.x) {
    //Right-Left aligned
    openSharedVert(a, b, (y) => {
      map.tiles[b.x + y * map.width].adj.right = aHigher
        ? AdjTypes.ONE_IN
        : AdjTypes.ONE_OUT;
      map.tiles[b.x - 1 + y * map.width].adj.left = aHigher
        ? AdjTypes.ONE_OUT
        : AdjTypes.ONE_IN;
    });
  } else if (a.y === b.y + b.h) {
    //Bot-Top aligned
    openSharedHorz(a, b, (x) => {
      map.tiles[x + a.y * map.width].adj.up = aHigher
        ? AdjTypes.ONE_OUT
        : AdjTypes.ONE_IN;
      map.tiles[x + (a.y - 1) * map.width].adj.down = aHigher
        ? AdjTypes.ONE_IN
        : AdjTypes.ONE_OUT;
    });
  } else if (a.y + a.h === b.y) {
    //Top-bot aligned
    openSharedHorz(a, b, (x) => {
      map.tiles[x + b.y * map.width].adj.up = aHigher
        ? AdjTypes.ONE_IN
        : AdjTypes.ONE_OUT;
      map.tiles[x + (b.y - 1) * map.width].adj.down = aHigher
        ? AdjTypes.ONE_OUT
        : AdjTypes.ONE_IN;
    });
  }
};

export const makeSubsetLedge = (
  rng: typeof RNG,
  map: ShrineMap,
  room: Bounds,
  minSize = 2
) => {
  const w = rng.getUniformInt(minSize, room.w - 1);
  const h = rng.getUniformInt(minSize, room.h - 1);
  const x = rng.getUniformInt(0, room.w - w) + room.x;
  const y = rng.getUniformInt(0, room.h - h) + room.y;
  setAreaZLevel(map, { x, y, w, h }, 2);
};

//The rooms must pass AABB already
export const makeZRamp = (
  map: ShrineMap,
  a: Bounds,
  b: Bounds,
  aHigher: boolean
) => {
  if (a.x === b.x + b.w) {
    //Left-Right aligned
    openSharedVert(a, b, (y) => {
      map.tiles[a.x + y * map.width].adj.left = aHigher
        ? AdjTypes.Z_OUT
        : AdjTypes.Z_IN;
      map.tiles[a.x - 1 + y * map.width].adj.right = aHigher
        ? AdjTypes.Z_IN
        : AdjTypes.Z_OUT;
    });
  } else if (a.x + a.w === b.x) {
    //Right-Left aligned
    openSharedVert(a, b, (y) => {
      map.tiles[b.x + y * map.width].adj.left = aHigher
        ? AdjTypes.Z_IN
        : AdjTypes.Z_OUT;
      map.tiles[b.x - 1 + y * map.width].adj.right = aHigher
        ? AdjTypes.Z_OUT
        : AdjTypes.Z_IN;
    });
  } else if (a.y === b.y + b.h) {
    //Bot-Top aligned
    openSharedHorz(a, b, (x) => {
      map.tiles[x + a.y * map.width].adj.up = aHigher
        ? AdjTypes.Z_OUT
        : AdjTypes.Z_IN;
      map.tiles[x + (a.y - 1) * map.width].adj.down = aHigher
        ? AdjTypes.Z_IN
        : AdjTypes.Z_OUT;
    });
  } else if (a.y + a.h === b.y) {
    //Top-bot aligned
    openSharedHorz(a, b, (x) => {
      map.tiles[x + b.y * map.width].adj.up = aHigher
        ? AdjTypes.Z_IN
        : AdjTypes.Z_OUT;
      map.tiles[x + (b.y - 1) * map.width].adj.down = aHigher
        ? AdjTypes.Z_OUT
        : AdjTypes.Z_IN;
    });
  }
};
